import './styles.scoped.css'
import {useState, useEffect} from 'react'
import {Typography, Button, Chip, IconButton, Menu, MenuItem, Skeleton} from '@mui/material'
import {Add, Edit, MoreVert} from '@mui/icons-material'
import {useNavigate, useParams} from 'react-router-dom'
import {useMobile} from '../../hooks'
import {useGlobalStates} from '../../contexts/GlobalStatesContext'
import {clientApi} from '../../services'
import {parseError, thousantSeparator, ignoreTimezone} from '../../utils/helpers'
import {format} from 'date-fns'
import {SkeletonContainer, TextSkeleton, ButtonSkeleton, ChipSkeleton} from '../../components'

const IconButtonSkeleton = () => <Skeleton variant="circular" width={24} height={24} sx={{m: 1}} />

// Mirrors the agreement cards: an active agreement with three service items and an older one with one.
const ServiceAgreementsSkeleton = ({mobile}) => (
    <SkeletonContainer>
        {[3, 1].map((items, index) => (
            <div key={index}>
                <br />
                {mobile ? (
                    <div className="card">
                        <div className="top">
                            <div className="top-left">
                                <TextSkeleton width={110} fontSize={16} />
                            </div>
                            <div className="top-right">
                                <ChipSkeleton width={index ? 92 : 84} height={32} />
                                <IconButtonSkeleton />
                            </div>
                        </div>
                        {[150, 70, 190].map((width, row) => (
                            <div className="content" key={row}>
                                <TextSkeleton width={[40, 76, 58][row]} fontSize={16} />
                                <TextSkeleton width={width} />
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="card">
                        <div className="top">
                            <div className="top-left">
                                <TextSkeleton width={290} fontSize={18} />
                                <TextSkeleton width={170} />
                            </div>
                            <div className="top-right">
                                <TextSkeleton width={100} />
                                <ChipSkeleton width={index ? 92 : 84} height={32} />
                                <IconButtonSkeleton />
                            </div>
                        </div>
                        {Array.from({length: items}).map((_, item) => (
                            <div key={item}>
                                <br />
                                <div className="content-container">
                                    <div className="content">
                                        <TextSkeleton width={[120, 140, 100][item]} fontSize={16} />
                                        <ChipSkeleton width={56} />
                                    </div>
                                    <div className="content">
                                        <TextSkeleton width={68} />
                                        <TextSkeleton width={76} />
                                        <TextSkeleton width={6} sx={{mx: 1.5}} />
                                        <TextSkeleton width={60} />
                                        <TextSkeleton width={76} />
                                    </div>
                                    <div className="content">
                                        <TextSkeleton width={78} />
                                        <TextSkeleton width={40} />
                                        <TextSkeleton width={6} sx={{mx: 1.5}} />
                                        <TextSkeleton width={36} />
                                        <TextSkeleton width={70} />
                                        <TextSkeleton width={6} sx={{mx: 1.5}} />
                                        <TextSkeleton width={72} />
                                        <TextSkeleton width={70} />
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        ))}
    </SkeletonContainer>
)

export default () => {
    const {setLoading, setMessageBox} = useGlobalStates()
    const [anchorEl, setAnchorEl] = useState(null)
    const [serviceAgreements, setServiceAgreements] = useState([])
    const [selectedRow, setSelectedRow] = useState(null)
    const [skeleton, setSkeleton] = useState(true)
    const {clientId} = useParams()
    const navigate = useNavigate()
    const {mobile} = useMobile()

    useEffect(() => {
        getServiceAgreements()
    }, [])

    // refresh: reload after a mutation behind the global spinner instead of the skeleton
    const getServiceAgreements = (refresh = false) => {
        const setPending = refresh ? setLoading : setSkeleton
        setPending(true)
        clientApi
            .getServiceAgreements(clientId, {include_items: true})
            .then(({data: {data}}) => {
                setPending(false)
                if (data) {
                    setServiceAgreements(
                        data.sort((a, b) => {
                            if (a.status !== b.status) {
                                return a.status === 'active' ? -1 : 1
                            }
                            return new Date(b.created_at) - new Date(a.created_at)
                        }),
                    )
                }
            })
            .catch(error => {
                setPending(false)
                const {title, description} = parseError(error)
                if (description) {
                    setMessageBox({status: 'error', title, description})
                }
            })
    }

    const menuOpen = (e, row) => {
        setAnchorEl(e.currentTarget)
        setSelectedRow(row)
    }

    const menuClose = () => {
        setAnchorEl(null)
    }

    const viewServiceAgreement = () => {
        menuClose()
        navigate(`/clients-management-nosublayout/${clientId}/service-agreement/view-service-agreement/${selectedRow?.id}`)
    }

    const updateServiceAgreement = () => {
        menuClose()
        navigate(`/clients-management-nosublayout/${clientId}/service-agreement/service-agreement-form/${selectedRow?.id}`)
    }

    const printPdf = () => {
        menuClose()
    }

    const removeServiceAgreement = () => {
        menuClose()
        setLoading(true)
        clientApi
            .deleteServiceAgreement(clientId, selectedRow?.id)
            .then(() => {
                setMessageBox({
                    status: 'success',
                    title: 'Service Agreement Removed!',
                    description: 'The service agreement has been successfully deleted.',
                })
                getServiceAgreements(true)
            })
            .catch(error => {
                setLoading(false)
                const {title, description} = parseError(error)
                if (description) {
                    setMessageBox({status: 'error', title, description})
                }
            })
    }

    return (
        <>
            {mobile ? (
                <>
                    {skeleton ? (
                        <SkeletonContainer>
                            <ButtonSkeleton width="100%" />
                        </SkeletonContainer>
                    ) : serviceAgreements?.findIndex(item => item?.status == 'active') > -1 ? (
                        <Button
                            variant="outlined"
                            startIcon={<Edit />}
                            onClick={() =>
                                navigate(
                                    `/clients-management-nosublayout/${clientId}/service-agreement/service-agreement-form/${serviceAgreements?.find(item => item?.status == 'active')?.id}`,
                                )
                            }
                            fullWidth>
                            Update Service Agreement
                        </Button>
                    ) : (
                        <Button
                            variant="contained"
                            startIcon={<Add />}
                            onClick={() => navigate(`/clients-management-nosublayout/${clientId}/service-agreement/service-agreement-form`)}
                            fullWidth>
                            Add Service Agreement
                        </Button>
                    )}
                </>
            ) : (
                <>
                    <div className="header">
                        <Typography variant="h6" color="text.primary">
                            Service Agreement
                        </Typography>
                        {skeleton ? (
                            <SkeletonContainer>
                                <ButtonSkeleton width={236} />
                            </SkeletonContainer>
                        ) : serviceAgreements?.findIndex(item => item?.status == 'active') > -1 ? (
                            <Button
                                variant="outlined"
                                startIcon={<Edit />}
                                onClick={() =>
                                    navigate(
                                        `/clients-management-nosublayout/${clientId}/service-agreement/service-agreement-form/${serviceAgreements?.find(item => item?.status == 'active')?.id}`,
                                    )
                                }>
                                Update Service Agreement
                            </Button>
                        ) : (
                            <Button
                                variant="contained"
                                startIcon={<Add />}
                                onClick={() => navigate(`/clients-management-nosublayout/${clientId}/service-agreement/service-agreement-form`)}>
                                Add Service Agreement
                            </Button>
                        )}
                    </div>
                </>
            )}
            <br />
            {skeleton && <ServiceAgreementsSkeleton mobile={mobile} />}
            {serviceAgreements?.map(serviceAgreement => (
                <div key={serviceAgreement?.id}>
                    {mobile ? (
                        <>
                            <br />
                            <div className="card">
                                <div className="top">
                                    <div className="top-left">
                                        <Typography variant="h8" color="primary">
                                            {'No.: ' + serviceAgreement?.agreement_number}
                                        </Typography>
                                    </div>
                                    <div className="top-right">
                                        {serviceAgreement?.status == 'active' && (
                                            <Chip
                                                label={`Active ${serviceAgreement?.service_items?.filter(item => item.status === 'active').length}/${serviceAgreement?.service_items?.length}`}
                                                color="success"
                                            />
                                        )}
                                        {serviceAgreement?.status == 'inactive' && <Chip label="Not Active" color="error" />}
                                        <IconButton onClick={e => menuOpen(e, serviceAgreement)}>
                                            <MoreVert />
                                        </IconButton>
                                    </div>
                                </div>
                                <div className="content">
                                    <Typography variant="h8" color="text.primary">
                                        Date:
                                    </Typography>
                                    <Typography variant="body2" color="text.primary">
                                        {format(new Date(ignoreTimezone(serviceAgreement?.effective_date)), 'MM/dd/yyyy') +
                                            ' - ' +
                                            format(new Date(ignoreTimezone(serviceAgreement?.through_date)), 'MM/dd/yyyy')}
                                    </Typography>
                                </div>
                                <div className="content">
                                    <Typography variant="h8" color="text.primary">
                                        Total Rate:
                                    </Typography>
                                    <Typography variant="body2" color="text.primary">
                                        ${thousantSeparator(serviceAgreement?.total_amount)}
                                    </Typography>
                                </div>
                                <div className="content">
                                    <Typography variant="h8" color="text.primary">
                                        Service:
                                    </Typography>
                                    <Typography variant="body2" color="text.primary">
                                        {serviceAgreement?.service_items?.length == 1 && serviceAgreement?.service_items[0]?.service_name}
                                        {serviceAgreement?.service_items?.length == 2 &&
                                            serviceAgreement?.service_items[0]?.service_name +
                                                ', ' +
                                                serviceAgreement?.service_items[1]?.service_name}
                                        {serviceAgreement?.service_items?.length > 2 &&
                                            serviceAgreement?.service_items[0]?.service_name +
                                                ', ' +
                                                serviceAgreement?.service_items[1]?.service_name +
                                                ', +' +
                                                (serviceAgreement?.service_items?.length - 2) +
                                                ' more'}
                                    </Typography>
                                </div>
                            </div>
                        </>
                    ) : (
                        <>
                            <br />
                            <div className="card">
                                <div className="top">
                                    <div className="top-left">
                                        <Typography variant="h7" color="primary">
                                            {'Service Agreement No.: ' + serviceAgreement?.agreement_number}
                                        </Typography>
                                        <Typography variant="body2" color="text.primary">
                                            {format(new Date(ignoreTimezone(serviceAgreement?.effective_date)), 'MM/dd/yyyy') +
                                                ' - ' +
                                                format(new Date(ignoreTimezone(serviceAgreement?.through_date)), 'MM/dd/yyyy')}
                                        </Typography>
                                    </div>
                                    <div className="top-right">
                                        <Typography variant="body2" color="text.primary">
                                            Total: ${thousantSeparator(serviceAgreement?.total_amount)}
                                        </Typography>
                                        {serviceAgreement?.status == 'active' && (
                                            <Chip
                                                label={`Active ${serviceAgreement?.service_items?.filter(item => item.status === 'active').length}/${serviceAgreement?.service_items?.length}`}
                                                color="success"
                                            />
                                        )}
                                        {serviceAgreement?.status == 'inactive' && <Chip label="Not Active" color="error" />}
                                        <IconButton onClick={e => menuOpen(e, serviceAgreement)}>
                                            <MoreVert />
                                        </IconButton>
                                    </div>
                                </div>
                                {serviceAgreement?.service_items?.map(serviceItem => (
                                    <div key={serviceItem?.id}>
                                        <br />
                                        <div className="content-container">
                                            <div className="content">
                                                <Typography variant="h8" color="secondary">
                                                    {serviceItem?.service_name}
                                                </Typography>
                                                {serviceItem?.status == 'active' && <Chip label="Active" color="success" size="small" />}
                                                {serviceItem?.status == 'pending' && <Chip label="Pending" color="warning" size="small" />}
                                                {serviceItem?.status == 'inactive' && <Chip label="Not Active" color="error" size="small" />}
                                            </div>
                                            <div className="content">
                                                <Typography variant="h9" color="text.primary">
                                                    Start Date:
                                                </Typography>
                                                <Typography variant="body2" color="text.primary">
                                                    {format(new Date(ignoreTimezone(serviceItem?.start_date)), 'MM/dd/yyyy')}
                                                </Typography>
                                                <Typography variant="body2" color="text.primary" style={{marginLeft: 12, marginRight: 12}}>
                                                    |
                                                </Typography>
                                                <Typography variant="h9" color="text.primary">
                                                    End Date:
                                                </Typography>
                                                <Typography variant="body2" color="text.primary">
                                                    {format(new Date(ignoreTimezone(serviceItem?.end_date)), 'MM/dd/yyyy')}
                                                </Typography>
                                            </div>
                                            <div className="content">
                                                <Typography variant="h9" color="text.primary">
                                                    Total Units :
                                                </Typography>
                                                <Typography variant="body2" color="text.primary">
                                                    {thousantSeparator(serviceItem?.total_units)}
                                                </Typography>
                                                <Typography variant="body2" color="text.primary" style={{marginLeft: 12, marginRight: 12}}>
                                                    |
                                                </Typography>
                                                <Typography variant="h9" color="text.primary">
                                                    Used:
                                                </Typography>
                                                <Typography variant="body2" color="text.primary">
                                                    {'$' +
                                                        thousantSeparator(serviceItem?.used_units) +
                                                        ' (' +
                                                        (serviceItem?.total_units == 0
                                                            ? 0
                                                            : Math.round((100 * serviceItem?.used_units) / serviceItem?.total_units)) +
                                                        '%)'}
                                                </Typography>
                                                <Typography variant="body2" color="text.primary" style={{marginLeft: 12, marginRight: 12}}>
                                                    |
                                                </Typography>
                                                <Typography variant="h9" color="text.primary">
                                                    Remaining:
                                                </Typography>
                                                <Typography variant="body2" color="text.primary">
                                                    {'$' +
                                                        thousantSeparator(serviceItem?.remaining_units) +
                                                        ' (' +
                                                        (serviceItem?.total_units == 0
                                                            ? 0
                                                            : Math.round((100 * serviceItem?.remaining_units) / serviceItem?.total_units)) +
                                                        '%)'}
                                                </Typography>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </>
                    )}
                </div>
            ))}
            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={menuClose}
                anchorOrigin={{vertical: 'bottom', horizontal: 'right'}}
                transformOrigin={{vertical: 'top', horizontal: 'right'}}>
                <div>
                    <MenuItem onClick={viewServiceAgreement}>View Service Agreement</MenuItem>
                    <MenuItem onClick={updateServiceAgreement}>Update Service Agreement</MenuItem>
                    <MenuItem onClick={printPdf}>Print PDF</MenuItem>
                    <MenuItem onClick={removeServiceAgreement} sx={theme => ({color: theme.palette.error.main})}>
                        Remove Service Agreement
                    </MenuItem>
                </div>
            </Menu>
        </>
    )
}
